import type { ReactNode } from "react";
import { useMemo } from "react";
import { Surface } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import { cn } from "@/lib/cn";
import { FrameRulerStrip } from "@/components/canvas/frame-ruler-strip";
import { FrameToolsLauncher } from "@/components/layout/frame-tools-launcher";
import { useCanvasStore } from "@/stores/canvas-store";

/**
 * Canvas left rail — unified vertical column combining the frame ruler strip
 * with canvas control buttons. Replaces the old floating pill toolbar.
 *
 * Layout (top → bottom):
 *   1. Active-page frame ticks + one marker per inactive time page
 *   2. Fit / zoom button (decimal zoom idle, Maximize icon on hover)
 *   3. Zoom in
 *   4. Zoom out
 *   5. Pan / focus modes
 *   6. Expandable frame tools (note, terminal, taskboard)
 *
 * The shell is a role-marked Surface. Web renders the shared fallback class;
 * desktop native mode measures the same role and lets AppKit own the material.
 */

/* ── Constants ── */
const BTN_SIZE = 36;
const BTN_GAP = 6;
const SECTION_GAP = 12;
const RULER_TOOLTIP_CLIP_RIGHT = 480;

/** Tooltip class — shared by rail buttons + ruler tick tooltips, must match. */
const TOOLTIP_CLASS =
  "denker-glass-tooltip pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-canvas-md font-medium opacity-0 transition-opacity group-hover:opacity-100";

interface CanvasLeftRailProps {
  /** Current zoom level as a percentage (e.g. 100) */
  zoomLevel?: number;
  onFitView?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onPanMode?: () => void;
  panModeActive?: boolean;
  panModeLabel?: string;
  focusActive?: boolean;
  onNotePlacement?: () => void;
  notePlacementActive?: boolean;
  onTerminalPlacement?: () => void;
  onTaskboardPlacement?: () => void;
  onMemoryPlacement?: () => void;
  topControls?: ReactNode;
  className?: string;
}

interface RailButtonProps {
  onClick?: () => void;
  label: string;
  testId: string;
  isActive?: boolean;
  children: ReactNode;
}

function RailButton({
  onClick,
  label,
  testId,
  isActive,
  children,
}: RailButtonProps) {
  const disabled = !onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      style={{ width: BTN_SIZE, height: BTN_SIZE }}
      className={cn(
        "group relative z-10 flex flex-shrink-0 items-center justify-center rounded-full bg-transparent p-0 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 active:scale-[0.92]",
        disabled ? "cursor-not-allowed" : "text-muted hover:text-primary",
        isActive && "text-accent hover:text-accent",
      )}
      aria-label={label}
      aria-pressed={isActive}
      data-testid={testId}
      data-active={isActive ? "true" : undefined}
    >
      <span
        aria-hidden="true"
        className={cn(
          "flex h-6 w-6 items-center justify-center rounded-full transition-colors",
          !disabled &&
            !isActive &&
            "group-hover:bg-[var(--color-interactive-hover)]",
          isActive && !disabled && "text-accent",
        )}
      >
        {children}
      </span>
      <span className={TOOLTIP_CLASS}>{label}</span>
    </button>
  );
}

/**
 * Fit/zoom button — displays the current zoom (decimal, e.g. "1.0") by
 * default, swaps to the Maximize icon on hover. Click fits the view.
 */
/**
 * Store-subscribing variant of FitZoomButton. Confines the
 * `viewport.zoom` subscription to this leaf so the rest of the rail
 * (notably the FrameRulerStrip) doesn't re-render on every zoom tick.
 * Used when the parent doesn't pass an explicit `zoomLevel` prop.
 */
function StoreFitZoomButton({ onFitView }: { onFitView?: () => void }) {
  const zoomLevel = useCanvasStore((s) => s.viewport.zoom * 100);
  return <FitZoomButton zoomLevel={zoomLevel} onFitView={onFitView} />;
}

function FitZoomButton({
  zoomLevel,
  onFitView,
}: {
  zoomLevel: number;
  onFitView?: () => void;
}) {
  const t = useT(canvasMessages);
  const disabled = !onFitView;
  return (
    <button
      type="button"
      onClick={onFitView}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      style={{ width: BTN_SIZE, height: BTN_SIZE }}
      className={cn(
        "group relative z-10 flex flex-shrink-0 items-center justify-center rounded-full bg-transparent p-0 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2",
        disabled ? "cursor-not-allowed" : "text-muted hover:text-primary",
      )}
      aria-label={t("fitView")}
      data-testid="rail-fit-view"
    >
      <span
        aria-hidden="true"
        className="flex h-6 w-6 items-center justify-center rounded-full transition-colors"
      >
        <span className="text-[9px] font-semibold leading-none tabular-nums group-hover:hidden">
          {(zoomLevel / 100).toFixed(1)}
        </span>
        <Icons.Maximize className="pointer-events-none hidden h-4 w-4 group-hover:block" />
      </span>
      <span className={TOOLTIP_CLASS}>{t("fitView")}</span>
    </button>
  );
}

type RailControlId = "fit" | "zoom-in" | "zoom-out" | "pan" | "frame-tools";

interface RailControl {
  id: RailControlId;
  section: "view" | "zoom" | "mode" | "create";
}

const RAIL_CONTROLS: RailControl[] = [
  { id: "fit", section: "view" },
  { id: "zoom-in", section: "zoom" },
  { id: "zoom-out", section: "zoom" },
  { id: "pan", section: "mode" },
  { id: "frame-tools", section: "create" },
];

function controlOffset(index: number) {
  let offset = 0;
  for (let i = 1; i <= index; i += 1) {
    const previous = RAIL_CONTROLS[i - 1];
    const current = RAIL_CONTROLS[i];
    offset += previous?.section === current?.section ? BTN_GAP : SECTION_GAP;
  }
  return offset;
}

export function CanvasLeftRail({
  zoomLevel,
  onFitView,
  onZoomIn,
  onZoomOut,
  onPanMode,
  panModeActive = false,
  panModeLabel,
  focusActive = false,
  onNotePlacement,
  notePlacementActive = false,
  onTerminalPlacement,
  onTaskboardPlacement,
  onMemoryPlacement,
  topControls,
  className,
}: CanvasLeftRailProps) {
  const t = useT(canvasMessages);
  const itemOffsets = useMemo(
    () => RAIL_CONTROLS.map((_, index) => controlOffset(index)),
    [],
  );
  const railHeight =
    RAIL_CONTROLS.length * BTN_SIZE +
    (itemOffsets[itemOffsets.length - 1] ?? 0);
  const zoomControlsDisabled = focusActive;

  return (
    <Surface
      surfaceRole="rail"
      nativeLevel="root"
      nativeGroup="left-rail"
      className={cn(
        "flex max-h-full min-h-0 flex-col items-center overflow-visible p-[3px]",
        className,
      )}
      data-fit-inset="left"
      data-testid="canvas-left-rail"
    >
      {topControls && <div className="mb-3 shrink-0 pb-3">{topControls}</div>}

      {/* Ruler navigation — this wrapper is the rail's only squeezable flex
       *  item (min-h-0); when entries exceed the work-area cap the strip measures
       *  this available lane and compresses every slot to fit. The clip path
       *  keeps tick paint inside the ruler lane while leaving room to the
       *  right for tooltips. */}
      <div
        className="mb-3 flex min-h-0 w-9 flex-1 flex-col overflow-visible pb-3 empty:mb-0 empty:flex-none empty:pb-0"
        style={{
          clipPath: `inset(0 -${RULER_TOOLTIP_CLIP_RIGHT}px 0 0)`,
        }}
        data-testid="rail-ruler-area"
      >
        <FrameRulerStrip />
      </div>

      {/* Button group — always visible, owns its own magnification context.
       *  When no explicit zoomLevel prop is supplied, the store-subscribing
       *  variant confines the viewport.zoom subscription to the leaf button
       *  so zoom ticks don't re-render the ruler strip above. */}
      <div
        className="relative flex shrink-0 flex-col items-center"
        style={{ height: railHeight }}
        data-testid="rail-control-area"
      >
        {zoomLevel === undefined ? (
          <StoreFitZoomButton onFitView={onFitView} />
        ) : (
          <FitZoomButton zoomLevel={zoomLevel} onFitView={onFitView} />
        )}

        <div style={{ marginTop: SECTION_GAP }}>
          <RailButton
            onClick={zoomControlsDisabled ? undefined : onZoomIn}
            label={t("zoomIn")}
            testId="toolbar-zoom-in"
          >
            <Icons.ZoomIn className="h-4 w-4" />
          </RailButton>
        </div>

        <div style={{ marginTop: BTN_GAP }}>
          <RailButton
            onClick={zoomControlsDisabled ? undefined : onZoomOut}
            label={t("zoomOut")}
            testId="toolbar-zoom-out"
          >
            <Icons.ZoomOut className="h-4 w-4" />
          </RailButton>
        </div>

        <div style={{ marginTop: SECTION_GAP }}>
          <RailButton
            onClick={onPanMode}
            label={panModeLabel ?? t("pan")}
            testId="toolbar-pan-mode"
            isActive={panModeActive}
          >
            <Icons.Hand className="h-4 w-4" />
          </RailButton>
        </div>

        <div style={{ marginTop: SECTION_GAP }}>
          <FrameToolsLauncher
            onNote={onNotePlacement}
            noteActive={notePlacementActive}
            onTerminal={onTerminalPlacement}
            onTaskboard={onTaskboardPlacement}
            onMemory={onMemoryPlacement}
          />
        </div>
      </div>
    </Surface>
  );
}

export type { CanvasLeftRailProps };
